import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator, Pressable, Linking, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { Plus, Loader2, ChevronRight, MessageSquare } from '../lib/icons';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { getAccessibleHomes } from '../lib/homeMembership';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import QuickStats from './dashboard/QuickStats';
import QuickActions from './dashboard/QuickActions';
import RecentActivity from './dashboard/RecentActivity';
import UpcomingTasks from './dashboard/UpcomingTasks';
import ImportantReminders from './dashboard/ImportantReminders';
import HomesList from './dashboard/HomesList';
import ScheduleConsultation from './dashboard/ScheduleConsultation';
import ServiceRequestHistory from './dashboard/ServiceRequestHistory';
import NextVisit from './dashboard/NextVisit';
import YourTeam from './dashboard/YourTeam';

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  square_footage: number | null;
  year_built: number | null;
}

interface PartialHome {
  id: string;
  address: string;
}

interface Task {
  id: string;
  task_id: string;
  next_due_date: string;
  status: string;
  maintenance_tasks: {
    name: string;
  };
}

interface Expense {
  amount: number;
  date: string;
  category: string;
}

interface HomeManager {
  profileId: string;
  fullName: string;
  profilePhotoUrl?: string | null;
  conversationId: string | null;
}

const Dashboard = () => {
  const { activeHome } = useActiveHome();
  const navigate = useNavigate();
  const [homes, setHomes] = useState<Home[]>([]);
  const [loading, setLoading] = useState(true);
  const [isHomesListOpen, setIsHomesListOpen] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [unconfiguredHomes, setUnconfiguredHomes] = useState<PartialHome[]>([]);
  const [homeManagers, setHomeManagers] = useState<HomeManager[]>([]);

  useEffect(() => {
    fetchHomes();
    fetchExpenses();
    fetchTasks();
    checkHomeSystems();
    fetchHomeManagers();
  }, [activeHome?.id]);

  const fetchHomes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const data = await getAccessibleHomes(user.id);
      setHomes(data as Home[]);
    } catch (error) {
      console.error('Error fetching homes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      // Get the current user's homes first
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const homes = await getAccessibleHomes(user.id);
      
      if (!homes || homes.length === 0) {
        setExpenses([]);
        return;
      }

      const homeIds = homes.map((home) => home.id);
      const yearStart = new Date(new Date().getFullYear(), 0, 1);

      const [{ data: legacyExpenses, error: legacyError }, { data: operationalExpenses, error: operationalError }] =
        await Promise.all([
          supabase
            .from('expenses')
            .select('amount, date, category')
            .in('home_id', homeIds)
            .gte('date', yearStart.toISOString())
            .order('date', { ascending: false }),
          supabase
            .from('service_request_expenses')
            .select('amount, category, description, expense_date, created_at, visit_id, service_request_id')
            .in('home_id', homeIds)
            .is('parent_expense_id', null)
            .eq('billable_to_homeowner', true)
            .order('created_at', { ascending: false }),
        ]);

      if (legacyError) throw legacyError;
      if (operationalError) throw operationalError;

      const mappedOperationalExpenses = (operationalExpenses || [])
        .map((expense) => ({
          amount: Number(expense.amount),
          date: expense.expense_date || expense.created_at,
          category:
            expense.category ||
            (expense.visit_id
              ? 'Visit Expense'
              : expense.service_request_id
                ? 'Service Request Expense'
                : 'Home Expense'),
        }))
        .filter((expense) => new Date(expense.date) >= yearStart);

      const combinedExpenses = [...(legacyExpenses || []), ...mappedOperationalExpenses].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setExpenses(combinedExpenses);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      // Get the current user's homes first
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const homes = await getAccessibleHomes(user.id);
      
      if (!homes || homes.length === 0) {
        setTasks([]);
        return;
      }

      // Then fetch maintenance plans for those homes
      const { data, error } = await supabase
        .from('maintenance_plans')
        .select('id, task_id, next_due_date, status, maintenance_tasks!inner(name)')
        .in('home_id', homes.map(home => home.id))
        .order('next_due_date', { ascending: true })
        .limit(5);

      if (error) throw error;
      
      // Transform the data to match expected format
      const transformedTasks = (data || []).map(item => ({
        id: item.id,
        task_id: item.task_id,
        next_due_date: item.next_due_date,
        status: item.status,
        maintenance_tasks: Array.isArray(item.maintenance_tasks) 
          ? item.maintenance_tasks[0] 
          : item.maintenance_tasks
      }));
      
      setTasks(transformedTasks as Task[]);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };


  const checkHomeSystems = async () => {
    try {
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Get homes owned by the current user
      const homes = await getAccessibleHomes(user.id);

      if (!homes || homes.length === 0) {
        setLoading(false);
        return;
      }

      // Get home systems configurations for the user's homes
      const { data: systems, error: systemsError } = await supabase
        .from('home_systems')
        .select('home_id, heating_systems, cooling_systems, appliances, other_systems, upgrades, outdoor_features')
        .in('home_id', homes.map(h => h.id));

      if (systemsError) throw systemsError;

      // Find homes without systems configured
      const unconfigured = homes.filter(home => {
        const homeSystem = systems?.find(s => s.home_id === home.id);
        if (!homeSystem) return true;

        // Check if any system array is empty or null
        const hasConfiguration = Object.values(homeSystem).some(value => 
          Array.isArray(value) && value.length > 0
        );
        return !hasConfiguration;
      });

      setUnconfiguredHomes(unconfigured);
    } catch (error) {
      console.error('Error checking home systems:', handleSupabaseError(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeManagers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user');
      }

      const userHomes = await getAccessibleHomes(user.id);

      // Group homes by manager name (skip null / Unassigned). Preserve the
      // order managers are first encountered for stable rendering.
      const managerToHomeIds = new Map<string, string[]>();
      for (const home of userHomes) {
        const name = home.home_manager;
        if (!name || name === 'Unassigned') continue;
        const existing = managerToHomeIds.get(name) || [];
        existing.push(home.id);
        managerToHomeIds.set(name, existing);
      }

      if (managerToHomeIds.size === 0) {
        setHomeManagers([]);
        return;
      }

      const managerNames = Array.from(managerToHomeIds.keys());
      const { data: managerProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, profile_photo_url')
        .in('full_name', managerNames)
        .eq('is_system_manager', true);

      if (profilesError) {
        console.error('Error fetching home manager profiles:', profilesError);
        return;
      }

      const profileByName = new Map(
        (managerProfiles || []).map((p) => [p.full_name, p])
      );

      const allHomeIds = Array.from(managerToHomeIds.values()).flat();
      const { data: conversations, error: conversationsError } = await supabase
        .from('conversations')
        .select('id, home_id')
        .eq('homeowner_id', user.id)
        .eq('type', 'home_manager')
        .in('home_id', allHomeIds);

      if (conversationsError) {
        console.error('Error fetching home manager conversations:', conversationsError);
      }

      const conversationByHomeId = new Map(
        (conversations || []).map((c) => [c.home_id as string, c.id as string])
      );

      const result: HomeManager[] = [];
      for (const [name, homeIds] of managerToHomeIds) {
        const profile = profileByName.get(name);
        if (!profile) continue;

        // When the same manager covers multiple homes there are multiple
        // per-home conversations. Pick the first that exists so the message
        // button has somewhere to land.
        let conversationId: string | null = null;
        for (const hid of homeIds) {
          const cid = conversationByHomeId.get(hid);
          if (cid) {
            conversationId = cid;
            break;
          }
        }

        result.push({
          profileId: profile.id,
          fullName: profile.full_name,
          profilePhotoUrl: profile.profile_photo_url,
          conversationId,
        });
      }

      setHomeManagers(result);
    } catch (error) {
      console.error('Error fetching home managers:', error);
    }
  };

  const renderManagerRow = (manager: HomeManager, label: string) => (
    <View key={manager.profileId} style={styles.homeManagerBanner}>
      <View style={styles.homeManagerBannerPressable}>
        <View style={styles.homeManagerContent}>
          {manager.profilePhotoUrl ? (
            <Image
              source={{ uri: manager.profilePhotoUrl }}
              accessibilityLabel={manager.fullName}
              style={styles.homeManagerPhoto as any}
            />
          ) : (
            <View style={styles.homeManagerPhotoPlaceholder}>
              <Text style={styles.homeManagerPhotoInitial}>
                {manager.fullName.charAt(0)}
              </Text>
            </View>
          )}
          <View style={styles.homeManagerInfo}>
            <Text style={styles.homeManagerLabel}>{label}</Text>
            <Text style={styles.homeManagerName}>
              {manager.fullName.split(' ')[0]}
            </Text>
            <Pressable onPress={() => Linking.openURL('sms:+16162363800')}>
              <Text style={styles.homeManagerPhone}>(616) 236-3800</Text>
            </Pressable>
          </View>
        </View>
      </View>
      {manager.conversationId && (
        <Pressable
          style={({ pressed }) => [
            styles.messageIconButton,
            pressed && styles.messageIconButtonPressed,
          ]}
          onPress={() => navigate(`/conversations/${manager.conversationId}`)}
        >
          <MessageSquare size={20} color="#d1d5db" />
        </Pressable>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <WebScrollView 
      style={styles.scrollView}
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
    >
      {/* Top Section with Black Background */}
      <View style={styles.topSectionWrapper}>
        <View style={styles.topSection}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Welcome back</Text>
              <Text style={styles.subtitle}>
                Here's what's happening with your {homes.length === 1 ? 'home' : 'homes'}
              </Text>
            </View>
          </View>

          {/* Home Manager(s) or Contact Banner */}
          {homeManagers.length === 1 ? (
            renderManagerRow(homeManagers[0], 'Your Home Manager')
          ) : homeManagers.length > 1 ? (
            <View style={styles.homeManagersGroup}>
              {homeManagers.map((manager, index) =>
                renderManagerRow(
                  manager,
                  index === 0 ? 'Your Home Managers' : 'Home Manager'
                )
              )}
            </View>
          ) : (
            <Pressable
              style={({ pressed }) => [
                styles.contactBanner,
                pressed && styles.contactBannerPressed
              ]}
              onPress={() => Linking.openURL('sms:+16162363800')}
            >
              <Text style={styles.contactText}>
                Text us anytime at{' '}
                <Text style={styles.phoneNumber}>(616) 236-3800</Text>
              </Text>
            </Pressable>
          )}
        </View>

      </View>

      {/* White Content Section with curved top */}
      <View style={styles.whiteSectionWrapper}>
        {Platform.OS === 'web' && (
          <svg
            width="100%"
            height="40"
            viewBox="0 0 1440 40"
            preserveAspectRatio="none"
            style={{ display: 'block' } as any}
          >
            <path
              d="M0,15 Q720,0 1440,15 L1440,40 L0,40 Z"
              fill="#ffffff"
            />
          </svg>
        )}
        <View style={styles.whiteSection}>
        {/* Important reminders */}
        <View style={styles.cardWrapper}>
          <ImportantReminders />
        </View>

        {/* Next Visit */}
        <View style={styles.nextVisit}>
          <NextVisit />
        </View>

        {/* Service Request History */}
        <View style={styles.requestHistory}>
          <ServiceRequestHistory />
        </View>

        {/* Your Team (Home Manager + Referring Real Estate Agent) */}
        <View style={styles.cardWrapper}>
          <YourTeam activeHomeId={activeHome?.id || null} />
        </View>

        {/* Quick Navigation */}
        <View style={styles.quickNavCard}>
          <Pressable
            style={({ pressed }) => [
              styles.navItem,
              styles.navItemWithBorder,
              pressed && styles.navItemPressed
            ]}
            onPress={() => navigate('/maintenance-plan')}
          >
            <Text style={styles.navItemText}>Maintenance Plan</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.navItem,
              styles.navItemWithBorder,
              pressed && styles.navItemPressed
            ]}
            onPress={() => navigate('/honeydolist')}
          >
            <Text style={styles.navItemText}>HoneyDone List</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.navItem,
              styles.navItemWithBorder,
              pressed && styles.navItemPressed
            ]}
            onPress={() => navigate('/inventory')}
          >
            <Text style={styles.navItemText}>Inventory</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.navItem,
              pressed && styles.navItemPressed
            ]}
            onPress={() => navigate('/profile')}
          >
            <Text style={styles.navItemText}>Profile</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </Pressable>
        </View>
        </View>
      </View>

      {/* Homes List Modal */}
      <HomesList
        homes={homes}
        onClose={() => setIsHomesListOpen(false)}
        onAddHome={() => setIsHomesListOpen(false)}
        onHomeUpdated={fetchHomes}
        isOpen={isHomesListOpen}
        allowAddHome={false}
      />
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    minHeight: 400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  container: {
    // No margins needed - parent has no padding now
  },
  topSectionWrapper: {
    position: 'relative',
  },
  topSection: {
    backgroundColor: '#000000',
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 16,
    ...(Platform.OS === 'web' && {
      paddingTop: 80, // 64px for navbar + 16px spacing
      paddingBottom: 20, // Reduced padding for the curve
    }),
    ...(Platform.OS !== 'web' && {
      paddingTop: 16,
    }),
  },
  whiteSectionWrapper: {
    position: 'relative',
  },
  whiteSection: {
    backgroundColor: '#ffffff',
    paddingTop: 16,
    paddingBottom: 32,
    gap: 32,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  subtitle: {
    color: '#9ca3af',
    marginTop: 4,
    fontSize: 16,
    marginBottom: 8
  },
  contactBanner: {
    backgroundColor: '#1f2937',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#374151',
    cursor: Platform.OS === 'web' ? 'pointer' : 'auto',
  },
  contactBannerPressed: {
    backgroundColor: '#111827',
  },
  contactText: {
    fontSize: 14,
    color: '#d1d5db',
    textAlign: 'left',
    marginBottom: 12
  },
  phoneNumber: {
    fontWeight: '600',
    color: '#ffffff',
    textDecorationLine: 'underline',
  },
  homeManagersGroup: {
    gap: 8,
  },
  homeManagerBanner: {
    backgroundColor: '#1f2937',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#374151',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  homeManagerBannerPressable: {
    flex: 1,
    cursor: Platform.OS === 'web' ? 'pointer' : 'auto',
  },
  homeManagerBannerPressed: {
    opacity: 0.8,
  },
  messageIconButton: {
    padding: 8,
    borderRadius: 6,
    cursor: Platform.OS === 'web' ? 'pointer' : 'auto',
  },
  messageIconButtonPressed: {
    backgroundColor: '#374151',
  },
  homeManagerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  homeManagerPhoto: {
    width: 48,
    height: 48,
    borderRadius: 24,
    objectFit: 'cover',
  },
  homeManagerPhotoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#374151',
    justifyContent: 'center',
    alignItems: 'center',
  },
  homeManagerPhotoInitial: {
    fontSize: 20,
    fontWeight: '600',
    color: '#d1d5db',
  },
  homeManagerInfo: {
    flex: 1,
  },
  homeManagerLabel: {
    fontSize: 12,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  homeManagerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 2,
  },
  homeManagerPhone: {
    fontSize: 14,
    color: '#d1d5db',
    textDecorationLine: 'underline',
    cursor: Platform.OS === 'web' ? 'pointer' : 'auto',
  },
  nextVisit: {
    marginTop: 0
  },
  requestHistory: {
    marginTop: 8
  },
  cardWrapper: {
    marginHorizontal: 16, // Add horizontal margin for cards that need spacing
  },
  quickNavCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginTop: 16,
    marginHorizontal: 16, // Add horizontal margin for cards that need spacing
    ...(Platform.OS === 'web' && {
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    }),
    overflow: 'hidden',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    cursor: Platform.OS === 'web' ? 'pointer' : 'auto',
  },
  navItemWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  navItemPressed: {
    backgroundColor: '#f9fafb',
  },
  navItemText: {
    fontSize: 16,
    color: '#374151',
    fontWeight: '500',
  },
  contentGrid: {
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      gap: 32,
    }),
    gap: 24,
  },
  leftColumn: {
    ...(Platform.OS === 'web' && {
      flex: 2,
    }),
    gap: 24,
  },
  rightColumn: {
    ...(Platform.OS === 'web' && {
      flex: 1,
    }),
    gap: 24,
  },
});

export default Dashboard;